/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import React from 'react';

/**
 * RichText — render leggero e sicuro (nessun HTML) del markdown "light" prodotto
 * dai modelli LLM: **grassetto**, *corsivo*, `codice`, titoli (#, ##, ###),
 * elenchi puntati (-, •, *) e numerati (1.). Tutto il resto resta testo puro.
 *
 * Evita che l'utente veda asterischi e cancelletti grezzi nelle risposte AI.
 */

const INLINE_RE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|(?<![*\w])\*[^*\n]+\*(?!\w))/g;

export const renderInline = (line) =>
  String(line ?? '').split(INLINE_RE).map((part, i) => {
    if (!part) return null;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) return <code key={i} className="rounded bg-muted px-1 py-0.5 text-[0.85em] font-mono">{part.slice(1, -1)}</code>;
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });

/** Rimuove i marcatori markdown lasciando solo il testo (per anteprime / aria-label). */
export const stripMarkdown = (text) =>
  String(text ?? '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/(?<![*\w])\*([^*\n]+)\*(?!\w)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();

export const RichText = ({ text, className = '', as: Tag = 'div', ...rest }) => {
  const lines = String(text ?? '').replace(/\r\n/g, '\n').split('\n');
  const nodes = [];
  let para = [];
  const flush = () => {
    if (para.length) {
      nodes.push(<p key={`p-${nodes.length}`}>{para.map((l, i) => <React.Fragment key={i}>{i > 0 && <br />}{renderInline(l)}</React.Fragment>)}</p>);
      para = [];
    }
  };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    if (!trimmed || trimmed === '---' || trimmed === '***') { flush(); return; }
    const h = /^(#{1,3})\s+(.*)$/.exec(trimmed);
    if (h) { flush(); nodes.push(<h4 key={`h-${i}`} className="font-display font-semibold text-[1.05em] pt-1">{renderInline(h[2])}</h4>); return; }
    const ul = /^[-•*]\s+(.*)$/.exec(trimmed);
    if (ul) { flush(); nodes.push(<p key={`ul-${i}`} className="pl-4 relative before:content-['•'] before:absolute before:left-1">{renderInline(ul[1])}</p>); return; }
    const ol = /^(\d{1,2})[.)]\s+(.*)$/.exec(trimmed);
    if (ol) { flush(); nodes.push(<p key={`ol-${i}`} className="pl-6 relative"><span className="absolute left-0 tabular-nums">{ol[1]}.</span>{renderInline(ol[2])}</p>); return; }
    para.push(line);
  });
  flush();
  return <Tag className={`space-y-1.5 leading-relaxed break-words ${className}`} {...rest}>{nodes}</Tag>;
};

export default RichText;
